import React from 'react';
import { BasicType } from '@core/constants';
import { createCustomBlock } from '@core/utils/createCustomBlock';
import { merge } from 'lodash';
import { t } from '@core/utils';
import { listingScopeCanvasClass } from '@core/utils/listingScopeCanvasClass';
import { getListingClassId } from '@core/utils/listingDataAttr';
import { classnames } from '@core/utils/classnames';
import { BasicBlock } from '@core/components/BasicBlock';
import { IWrapper, WRAPPER_DEFAULT_ATTRIBUTES, resolveWrapperAttributes } from '../Wrapper';

// Declared locally (like the other listing blocks) to avoid circular deps.
const CustomBlockType = {
	CUSTOM_LISTING_BLOCK: 'custom_listing_block' as const,
};

// The Listing Wrapper: an ordinary mj-wrapper that also owns a listing. Build
// inside it with the normal section layouts — it behaves like any other
// wrapper, styling and all. Everything nested in it hydrates from the one
// listing picked on it, so columns inside always inherit and never carry their
// own (the per-column toggle is for columns OUTSIDE a wrapper).
export type ICustomListing = IWrapper &
	Record<'data', { value: { selectedListingId?: string; selectedListing?: Record<string, unknown> | null } }>;

export const CustomListingBlock = createCustomBlock<ICustomListing>({
	// Drives the canvas toolbar tab, the layer tree and the attribute drawer
	// ("… Attributes"). The sidebar tile is separate — it passes its own title
	// ("Listing Wrapper") in buildCategories, and product wants that shorter.
	get name() {
		return t('Custom Listing Wrapper');
	},
	type: CustomBlockType.CUSTOM_LISTING_BLOCK,
	create: payload => {
		const defaultData = {
			type: CustomBlockType.CUSTOM_LISTING_BLOCK,
			data: {
				value: {
					selectedListingId: '',
					selectedListing: null,
				},
			},
			attributes: {
				// Same defaults as the regular Wrapper, padding included — a Listing
				// Wrapper should drop in looking like the wrapper it is a variant of.
				...WRAPPER_DEFAULT_ATTRIBUTES,
			},
			children: [],
		} as unknown as ICustomListing;
		return merge(defaultData, payload);
	},
	// A wrapper: page-level only, holding sections. mj-wrapper cannot nest
	// inside another wrapper.
	validParentType: [BasicType.PAGE],
	render(params) {
		const { data } = params;
		// Canvas only: the listing badge. Deliberately NOT full-width — that's an
		// mj-wrapper attribute, and setting it made this render edge-to-edge
		// instead of at the email's content width like every other wrapper.
		let attributes = listingScopeCanvasClass(
			resolveWrapperAttributes(params),
			params,
		);

		const boundListingId = data.data.value.selectedListingId;
		// mj-wrapper's own attribute schema drops data-* attributes the same way
		// mj-image does, so the binding can't be set directly here - css-class is
		// the one attribute schema that survives, matched below (JsonToMjml.tsx)
		// via mj-html-attributes/mj-selector to re-attach data-listing-id onto
		// this same compiled element, same treatment Image/index.tsx gives
		// data-dynamic-tag on the <img>.
		if (params.mode === 'production' && boundListingId) {
			attributes = {
				...attributes,
				'css-class': classnames(attributes['css-class'], getListingClassId(params.idx)),
			};
		}

		return (
			<BasicBlock
				params={{
					...params,
					data: { ...data, attributes },
				}}
				tag='mj-wrapper'
			/>
		);
	},
});
